import * as React from 'react';
import { DatePicker, DateRangePicker } from '@components/base/inputs';
import type { DateRange } from '@components/base/inputs';
import { VStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['sm', 'md', 'lg'] as const;

function DatePickerShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [policyStartDate, setPolicyStartDate] = React.useState<Date | null>(null);
  const [policyRange, setPolicyRange] = React.useState<DateRange>({ start: null, end: null });
  const [claimDate, setClaimDate] = React.useState<Date | null>(null);

  // Calculate min/max dates for examples
  const today = new Date();
  const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  const oneYearFromNow = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
  const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="date picker">
          <div className="max-w-md">
            <DatePicker
              label="Policy Start Date"
              placeholder="Select date"
              size={playgroundProps.size as 'sm' | 'md' | 'lg'}
              disabled={playgroundProps.disabled as boolean}
              required={playgroundProps.required as boolean}
              value={selectedDate}
              onChange={setSelectedDate}
            />
          </div>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Date pickers come in three sizes: small (32px), medium (40px), and large (48px)"
       
      >
          <VStack gap={4} className="max-w-md">
            {sizes.map((size) => (
              <DatePicker
                key={size}
                label={`${size.toUpperCase()} Size`}
                placeholder="Select date"
                size={size}
              />
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* With Labels */}
      <ShowcaseDemoSection
        title="With Labels"
        description="Date pickers with descriptive labels and helper text"
       
      >
          <VStack gap={4} className="max-w-md">
            <DatePicker
              label="Date of Birth"
              placeholder="MM/DD/YYYY"
              helperText="Enter your date of birth for verification"
            />
            <DatePicker
              label="Incident Date"
              placeholder="When did the incident occur?"
              helperText="Enter the date the incident happened"
              value={claimDate}
              onChange={setClaimDate}
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Required Fields */}
      <ShowcaseDemoSection
        title="Required Fields"
        description="Required date fields display an asterisk indicator"
       
      >
          <VStack gap={4} className="max-w-md">
            <DatePicker
              label="Effective Date"
              placeholder="Select date"
              required
              value={policyStartDate}
              onChange={setPolicyStartDate}
            />
            <DatePicker
              label="Expiration Date"
              placeholder="Select date"
              required
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Error State */}
      <ShowcaseDemoSection
        title="Error State"
        description="Date pickers with validation errors"
       
      >
          <VStack gap={4} className="max-w-md">
            <DatePicker
              label="Claim Date"
              placeholder="Select date"
              error
              errorMessage="Claim date cannot be in the future"
            />
            <DatePicker
              label="Policy Start"
              placeholder="Select date"
              error
              errorMessage="Start date must be within 30 days"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Disabled State */}
      <ShowcaseDemoSection
        title="Disabled State"
        description="Disabled date pickers cannot be interacted with"
       
      >
          <VStack gap={4} className="max-w-md">
            <DatePicker
              label="Policy Effective Date"
              value={today}
              disabled
              helperText="Effective date is set by the system"
            />
            <DatePicker
              label="Locked Date"
              placeholder="Select date"
              disabled
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Min/Max Dates */}
      <ShowcaseDemoSection
        title="Min/Max Date Constraints"
        description="Date pickers can have minimum and maximum date restrictions"
       
      >
          <VStack gap={4} className="max-w-md">
            <DatePicker
              label="Future Dates Only"
              placeholder="Select a future date"
              minDate={today}
              helperText="Policy must start today or later"
            />
            <DatePicker
              label="Past Dates Only"
              placeholder="Select a past date"
              maxDate={today}
              helperText="Incident must have already occurred"
            />
            <DatePicker
              label="Within Next 30 Days"
              placeholder="Select date"
              minDate={today}
              maxDate={thirtyDaysFromNow}
              helperText="Effective date must be within the next 30 days"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Date Formats */}
      <ShowcaseDemoSection
        title="Date Formats"
        description="Date pickers support different date format displays"
       
      >
          <VStack gap={4} className="max-w-md">
            <DatePicker
              label="US Format (MM/DD/YYYY)"
              placeholder="MM/DD/YYYY"
              format="MM/dd/yyyy"
            />
            <DatePicker
              label="European Format (DD/MM/YYYY)"
              placeholder="DD/MM/YYYY"
              format="dd/MM/yyyy"
            />
            <DatePicker
              label="ISO Format (YYYY-MM-DD)"
              placeholder="YYYY-MM-DD"
              format="yyyy-MM-dd"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Date Range Picker */}
      <ShowcaseDemoSection
        title="Date Range Picker"
        description="Select a range of dates with start and end inputs"
       
      >
          <VStack gap={4} className="max-w-lg">
            <DateRangePicker
              label="Policy Period"
              value={policyRange}
              onChange={setPolicyRange}
              helperText="Select the coverage period for your policy"
            />
            <DateRangePicker
              label="Claim Period"
              startPlaceholder="From date"
              endPlaceholder="To date"
              helperText="Enter the date range for the claim"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Date Range with Constraints */}
      <ShowcaseDemoSection
        title="Date Range with Constraints"
        description="Date range pickers with min/max date restrictions"
       
      >
          <VStack gap={4} className="max-w-lg">
            <DateRangePicker
              label="Coverage Period (This Year)"
              minDate={oneYearAgo}
              maxDate={oneYearFromNow}
              helperText="Coverage period must be within one year from today"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Date Range Required */}
      <ShowcaseDemoSection
        title="Required Date Range"
        description="Date range pickers that require selection"
       
      >
          <VStack gap={4} className="max-w-lg">
            <DateRangePicker
              label="Rental Period"
              required
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Date Range Error */}
      <ShowcaseDemoSection
        title="Date Range Error State"
        description="Date range pickers with validation errors"
       
      >
          <VStack gap={4} className="max-w-lg">
            <DateRangePicker
              label="Invalid Range"
              error
              errorMessage="End date must be after start date"
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Date Range Disabled */}
      <ShowcaseDemoSection
        title="Disabled Date Range"
        description="Disabled date range pickers"
       
      >
          <VStack gap={4} className="max-w-lg">
            <DateRangePicker
              label="Locked Coverage Period"
              value={{
                start: today,
                end: new Date(today.getFullYear(), today.getMonth() + 6, today.getDate()),
              }}
              disabled
              helperText="Coverage period cannot be modified"
            />
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const DatePickerShowcase: ShowcaseDefinition = {
  id: 'date-picker',
  name: 'DatePicker',
  category: 'inputs',
  description: 'Date selection with calendar popup',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the date picker (sm=32px, md=40px, lg=48px)',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the date picker is disabled',
    },
    {
      name: 'required',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the field is required (shows asterisk)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-expanded (calendar visibility)',
      'aria-haspopup="dialog"',
      'aria-controls (calendar reference)',
      'aria-label on calendar button',
      'aria-invalid (error state)',
      'aria-required (required state)',
      'aria-describedby (helper/error text)',
    ],
    keyboardNav: [
      'Tab: Move focus to/from input',
      'Enter/Space: Open calendar',
      'Arrow keys: Navigate calendar days',
      'Page Up/Down: Navigate months',
      'Home/End: Go to start/end of month',
      'Escape: Close calendar',
    ],
    notes: [
      'Calendar opens as a modal dialog',
      'Focus trapped within calendar when open',
      'Today button for quick selection',
      'Visual feedback for selected and disabled dates',
    ],
  },
  component: DatePickerShowcaseComponent,
};
